import { useToast } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import { magnifyValue, STOCK_UI } from '@diamond/shared/decimal-util-web';
import {
  BackofficeActivitiesOrderItem,
  BackofficeOrderUpdate,
  BackofficeRevalidateUpdate,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { dinero, toDecimal } from 'dinero.js';
import { useState } from 'react';
import { NumericFormat } from 'react-number-format';

import { ORDER_CHANGELOG_QUERY_KEY } from '../../../api';
import {
  useUpdateActivitiesDetailColumn,
  useUpdateActivitiesDetailQueryData,
} from '../../../hooks';
import { EditableColumnProps } from '../../../types/activity-column-types';
import { ChangeQuantityModal } from '../../change-quantity-modal';

const API_URL = 'backoffice/order';

interface Props
  extends EditableColumnProps<
    BackofficeActivitiesOrderItem,
    number | undefined
  > {
  queryKey?: QueryKey;
  activitiesId: string;
  token?: string;
  refetch: () => void;
}

export function ConfirmedQuantityColumn({
  info,
  selectedData,
  onUpdate,
  queryKey = [],
  activitiesId,
  token,
  refetch,
}: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [openModalPromo, setOpenModalPromo] = useState<boolean>(false);
  const [values, setValues] = useState<string>('');

  const { patchData } = PatchDataWrapper<
    BackofficeOrderUpdate & BackofficeRevalidateUpdate
  >(API_URL, token as string, 'admin');

  const isSelected = selectedData.some(
    (row) => row.original.id === info.row.original.id
  );

  // Use confirmed_quantity (from accessor via info.getValue()) instead of quantity
  // This matches the backup logic: use quantity_free_goods if present, otherwise confirmed_quantity
  const rawQuantityItem =
    info.row.original.quantity_free_goods && Number(info.getValue()) > 0
      ? info.row.original.quantity_free_goods
      : Number(info.getValue() ?? 0);

  const quantityItem = Number(
    toDecimal(
      dinero({
        amount: rawQuantityItem,
        currency: STOCK_UI,
      })
    )
  );

  // Optimistic update for query cache
  const setQueryData = useUpdateActivitiesDetailQueryData({
    queryKey,
    info,
    name: 'confirmed_quantity',
  });

  // Debounced update handler
  const setValue = useUpdateActivitiesDetailColumn({
    onUpdate(value) {
      const confirmQty = magnifyValue(value, 'stock');
      setQueryData(confirmQty.magnifiedValue);
      patchData(
        { confirmed_quantity: confirmQty.magnifiedValue },
        undefined,
        `${activitiesId}/item/${info.row.original.id}`
      ).then(async (res) => {
        if (res?.empty_stock?.status === true) {
          showToast(
            toast,
            'error',
            'Mohon maaf stok barang sudah habis, hubungi admin SAP'
          );
        }
        refetch();
        await queryClient.invalidateQueries({
          queryKey: [ORDER_CHANGELOG_QUERY_KEY],
        });
      });
    },
  });

  if (isSelected) {
    const handleNumericChange = (numericValues: { value: string }) => {
      if (numericValues.value !== '') {
        // Revalidate first before updating
        patchData(
          '',
          `${activitiesId}/item/${info.row.original.id}/revalidate`
        ).then(async (res) => {
          if (res?.data?.is_valid === true) {
            setValue(numericValues.value);
          } else {
            setValues(numericValues.value);
            setOpenModalPromo(true);
          }
        });
      }
    };

    return (
      <div className="px-1">
        <NumericFormat
          key={`qty-${info.row.original.id}-${quantityItem}`}
          thousandSeparator="."
          decimalSeparator=","
          style={{
            borderColor: 'gray',
            borderWidth: 1,
            borderRadius: 4,
            padding: 4,
          }}
          allowNegative={false}
          defaultValue={quantityItem}
          onValueChange={handleNumericChange}
        />
        <ChangeQuantityModal
          isOpen={openModalPromo}
          onClose={() => setOpenModalPromo(false)}
          text={info.row.original.mechanism ?? ''}
          duration={''}
          onSubmit={() => setValue(values)}
          item={{
            id: info.row.original.product_id
              ? info.row.original.product_id
              : '',
            img: info.row.original.product_images
              ? info.row.original.product_images[0]
              : '',
            price: info.row.original.price,
            qty: (info.row.original.quantity_free_goods || 0) / 1000,
            uom: info.row.original.sales_unit_desc,
          }}
          isDiscount={
            info.row.original.is_discount === undefined ? false : true
          }
        />
      </div>
    );
  }

  return (
    <NumericFormat
      value={quantityItem}
      displayType={'text'}
      thousandSeparator="."
      decimalSeparator=","
    />
  );
}
